define('services/communicationService',[
    'lib/bluebird',
    'lib/lodash4',
    'lib/knockout',
    'models/Call',
    'models/Chat',
    'services/loggingService',
    'services/apiCore',
    'services/liveDataService',
    'services/authService'
], function (Promise, _, ko, Call, Chat, LOG, apiCore, liveDataService, authService) {
    'use strict';

    // The maximum time in milliseconds to wait for confirmation of a communication action via push
    // notification before falling back on an API check. Anthony Roach of conversation service fame
    // said 15 seconds was a good number.
    var VALIDATION_WAIT_TIME = 15000;

    var ParticipantProps = {
        STATUS: 'status',
        HELD: 'held',
        RECORD: 'recording'
    };

    var Status = {
        CONNECTED: 'connected',
        DISCONNECTED: 'disconnected'
    };

    var RecordingStateProps = {
        NONE: 'none',
        PAUSED: 'paused',
        ACTIVE: 'active' // 'ACTIVE' resumes any paused recordings
    };

    /**
     * If communication data exists in activeCommunications, merge raw data into observable model (via fill() in ObservableModel.js)
     * Otherwise, add CommunicationData to activeCommunications
     * @param  {Observable Model} communicationModel - Communication Model (e.g., Call or Chat)
     * @param  {Observable Array} activeCommunications - Active Communications (could be calls, chats, or other)
     * @param  {Object} communicationData - Data received from an request for current communications
     * @return {Object} communication
     */
    var fillToActiveCommunications = function (CommunicationModel, activeCommunications, communicationData) {

        var communication = _.find(activeCommunications(), {id: communicationData.id});
        if (communication) {
            LOG.debug('Filling data into active communications', communicationData);
            CommunicationModel.merge(communication, communicationData);
        } else {
            LOG.debug('Adding to communications', communicationData);
            communication = addToActiveCommunications(CommunicationModel, activeCommunications, communicationData);
        }
        return communication;
    };


    /**
     * Add communications data to the active communications observable
     * @param {Observable Model} communicationModel - Communication Model (e.g., Call or Chat)
     * @param {Observable Array} activeCommunications - Observable containing active communications
     * @param {Object} communicationData - Object containing raw communication data
     * @param {} [varname] [description]
     */
    var addToActiveCommunications = function (CommunicationModel, activeCommunications, communicationData) {
        var communication = new CommunicationModel(communicationData);
        activeCommunications.push(communication);
        LOG.debug('Comminication state tracked in active communications', communication.id);
        return communication;
    };

    /**
     * Do a GET over and over until validate() returns true on whatever was gotten, or until too many attempts have been made.
     * @param uri Which URI to GET
     * @param validate A function to pass whatever was gotten to
     */
    var poll = function(uri, validate) {
        return new Promise(function(resolve, reject) {
            var done = false;

            setTimeout(function () {
                done = true;
                reject(new Error("Timeout polling recording state"));
            }, 10000);

            (function innerPoll() {
                return apiCore.v2.get(uri).then(function(got) {
                    if (validate(got)) {
                        done = true;
                        resolve();
                    }
                }).finally(function() {
                    if (!done) {
                        innerPoll();
                    }
                });
            })();
        });
    };

    /**
     * Provides common functionality for services that deal with different communication types.
     *
     */
    return {
        createUriBuilder: function(baseUri) {
            return {
                baseUri: baseUri,

                base: function () {
                    return this.baseUri;
                },
                communication: function (id) {
                    return this.baseUri + '/' + id;
                },
                participant: function (id, participantId) {
                    return this.communication(id) + '/participants/' + participantId;
                },
                participantDetails: function (id, participantId) {
                    return this.participant(id, participantId) + '/details';
                },
                replace: function (id, participantId) {
                    return this.participant(id, participantId) + '/replace';
                },
                monitor: function (id, participantId) {
                    return this.participant(id, participantId) + '/monitor';
                },
                wrapUpCodes: function (id) {
                    return this.communication(id) + '/wrapupCodes';
                },
                wrapUp: function (id, participantId) {
                    return this.participant(id, participantId) + '/wrapup';
                },
                tags: function (id) {
                    return this.communication(id) + '/tags';
                },
                communicationsByType: function (type) {
                    return this.baseUri + '?communicationType=' + type;
                }
            };
        },

        getCommunication: function (uriBuilder, id) {
            return apiCore.v2.get(uriBuilder.communication(id));
        },

        disconnect: function (uriBuilder, id, participantId, activeCommunications, CommunicationModel) {
            var self = this;

            return apiCore.v2.patch(uriBuilder.participant(id, participantId), {
                state: Status.DISCONNECTED
            })
                .then(function () {
                    return self.validateActionSucceeded(id, participantId, CommunicationModel,
                        ParticipantProps.STATUS, Status.DISCONNECTED, activeCommunications, uriBuilder);
                });
        },

        /**
         * Begin Participant-level recording
         */
        startRecording: function (uriBuilder, id, activeCommunications, CommunicationModel) {
            var self = this;
            var communication = _.find(activeCommunications(), {id: id});
            var participantId = communication.participantForCurrentUser().id;

            return apiCore.v2.patch(uriBuilder.participant(id, participantId), {
                recording: true
            })
                .then(function () {
                    return self.validateActionSucceeded(id, participantId, CommunicationModel, ParticipantProps.RECORD, true,
                        activeCommunications, uriBuilder);
                });
        },

        /**
         * Begin secure pause of all participants' recordings
         */
        pauseRecording: function (uriBuilder, id) {
            var communicationDelta = { recordingState: RecordingStateProps.PAUSED };
            return apiCore.v2.patch(uriBuilder.communication(id), communicationDelta)
                .then(function() {
                    return poll(uriBuilder.communication(id), function verifyPauseRequest(communication) {
                        return communication.recordingState.toLowerCase() !== RecordingStateProps.ACTIVE;
                    });
                });
        },

        /**
         * End secure pause of all participants' recordings
         */
        unpauseRecording: function(uriBuilder, id) {
            var communicationDelta = { recordingState: RecordingStateProps.ACTIVE };
            return apiCore.v2.patch(uriBuilder.communication(id), communicationDelta)
                .then(function() {
                    return poll(uriBuilder.communication(id), function verifyUnpauseRequest(communication) {
                        return communication.recordingState.toLowerCase() !== RecordingStateProps.PAUSED;
                    });
                });
        },

        subscribeToTopic: function (topic, activeCommunications, getActiveCommunications, CommunicationModel) {

            var onNewUserCommunication = function (communication) {
                LOG.debug('Communication state tracked in active communication list', communication.id);
            };

            ko.computed(function () {
                var userId = authService.principalId(),
                    subscriptionsAvailable = liveDataService.connected();

                if (!userId) {
                    //if not logged in, cancel call tracking & clear active calls.
                    liveDataService.cancelLiveData(activeCommunications)
                        .then(function () {
                            activeCommunications.removeAll();
                        });
                } else {
                    if (subscriptionsAvailable) {
                        //If push notifications are available, start the subscription.
                        var topicId = 'v2.users.' + authService.principalId() + '.' + topic;
                        liveDataService.getLiveData(topicId, activeCommunications, {
                            onAdd: onNewUserCommunication
                        })
                        .then(function() {
                            if (getActiveCommunications) {
                                getActiveCommunications(userId)
                                .then(function(communications) {
                                    _.each(communications.entities, function(entity) {
                                        fillToActiveCommunications(CommunicationModel, activeCommunications, entity);
                                    });
                                });
                            }

                        });
                    }
                }
            });
        },


        validateActionSucceeded: function (id,
                                            participantId,
                                            CommunicationModel,
                                            participantAttributeToMonitor,
                                            expectedValue,
                                            activeCommunications,
                                            uriBuilder) {
            var self = this;
            var promiseResolver = Promise.defer();
            var subscription;
            var apiConfirmTimeout;
            var apiConfirmXHR;

            // Check a participant and return true if it's the participant we expect to see
            // change, and it has changed in the expected way
            function actionHasSucceded(participant) {
                var attributeValue;
                if (participant.id == participantId) {
                    attributeValue = ko.unwrap(participant[participantAttributeToMonitor]);

                    if (attributeValue.toLowerCase() === expectedValue.toLowerCase()) {
                        return true;
                    }
                }

                return false;
            }

            // Cleans up subscriptions, timeouts, etc created by this process
            function cleanUp() {
                if (subscription) {
                    subscription.dispose();
                }
                if (apiConfirmTimeout) {
                    clearTimeout(apiConfirmTimeout);
                }
                if (apiConfirmXHR) {
                    apiConfirmXHR.cancel();
                }
            }

            // Resolves the promise chain successfully & cleans up.
            function resolveSuccess() {
                promiseResolver.resolve();
                cleanUp();
            }

            // Watches for a push notification confirming the requested action
            function watchForSuccessUpdate() {
                return participant[participantAttributeToMonitor].subscribe(function () {
                    if (actionHasSucceded(participant)) {
                        resolveSuccess();
                    }
                });
            }

            // Uses the public API to to verify the requested action completed
            function verifyViaAjax() {
                apiConfirmXHR = self.getCommunication(uriBuilder, id);

                apiConfirmXHR
                    .then(function (communicationObj) {
                        var newCommunicationModel = new CommunicationModel(communicationObj);
                        var trackedCommunication = _.find(activeCommunications(), {id: id});
                        // If we're still tracking the communication, check the status
                        if (trackedCommunication) {
                            var newValue = null;
                            var successParticipant = _.find(newCommunicationModel.participants(), actionHasSucceded);

                            if (successParticipant) {
                                var trackedParticipant = _.find(trackedCommunication.participants(), {id: participantId});
                                newValue = ko.unwrap(successParticipant[participantAttributeToMonitor]);
                                promiseResolver.resolve();
                                trackedParticipant[participantAttributeToMonitor](newValue);
                            } else {
                                promiseResolver.reject({
                                    type: 'application'
                                });
                            }
                        }

                        // If we're not tracking the communication anymore, assume success
                        promiseResolver.resolve();
                    })
                    .catch(Promise.CancellationError, function () {
                        // Cancelled requests resolve the main promise elsewhere
                        return;
                    })
                    .catch(function (err) {
                        promiseResolver.reject(err);
                    })
                    .finally(function () {
                        cleanUp();
                    });
            }

            // Find the locally tracked version of the communication being updated
            var communication = _.find(activeCommunications(), {id: id});

            if (!communication) {
                // If we're not tracking the communication, there's no need to go through the validation process
                resolveSuccess();
            } else {
                // If we're tracking the communcation, we need to validate success or failure
                var participant = _.find(communication.participants(), {id: participantId});
                if (actionHasSucceded(participant)) {
                    // If the notification came in while the original ajax message was still in round trip, we're done .
                    resolveSuccess();
                } else {
                    // Otherwise, we have to watch for the change to occur
                    subscription = watchForSuccessUpdate();

                    // If too much time goes by without a push update, verify via ajax
                    apiConfirmTimeout = setTimeout(verifyViaAjax, VALIDATION_WAIT_TIME);
                }
            }

            return promiseResolver.promise;
        }
    };
});

